import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Search, Trash2, Plus, Upload, Video, Loader2, Shield, Library, TrendingUp, Settings } from 'lucide-react';
import { PlatinumContentManager } from '@/pages/platinum/PlatinumContentManager';
import { PlatinumProgress } from '@/pages/platinum/PlatinumProgress';

const panelStyle = {
  background: 'rgba(255,253,248,0.75)', border: '1px solid rgba(201,168,76,0.35)',
  borderRadius: 16, padding: '1.5rem', marginBottom: '1.5rem',
};

const AREAS = [
  { key: 'content', label: 'Training Content', icon: Library },
  { key: 'progress', label: 'Progress', icon: TrendingUp },
  { key: 'settings', label: 'Sanctuary Settings', icon: Settings },
];

export const PlatinumAdmin = ({ isPlatformAdmin }) => {
  const [area, setArea] = useState('content');
  return (
    <div data-testid="platinum-admin-area" style={{ maxWidth: area === 'settings' ? 820 : 1100, margin: '0 auto' }}>
      <h1 style={{ fontSize: '2.2rem', fontWeight: 700, color: '#2A2620', marginBottom: '.25rem', display: 'flex', alignItems: 'center', gap: '.6rem' }}>
        <Shield style={{ color: '#C9A84C' }} /> Platinum+ Admin
      </h1>
      <p style={{ color: '#6a6252', marginBottom: '1.25rem', fontStyle: 'italic' }}>
        Everything in here stays in here. Platinum+ content and progress are managed separately from the main platform.
      </p>

      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {AREAS.map((a) => {
          const Icon = a.icon;
          const on = area === a.key;
          return (
            <button key={a.key} onClick={() => setArea(a.key)} data-testid={`platinum-admin-area-${a.key}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '.45rem', padding: '.55rem 1.2rem',
                borderRadius: 999, fontWeight: 600, fontSize: '1rem',
                background: on ? 'linear-gradient(135deg,#C9A84C,#B9982F)' : 'rgba(255,253,248,0.8)',
                color: on ? '#fff' : '#6a6252',
                border: `1px solid ${on ? 'transparent' : 'rgba(201,168,76,0.4)'}`,
              }}>
              <Icon className="w-4 h-4" /> {a.label}
            </button>
          );
        })}
      </div>

      {area === 'content' && <PlatinumContentManager />}
      {area === 'progress' && <PlatinumProgress />}
      {area === 'settings' && (
        <>
          <IntroVideoManager />
          {isPlatformAdmin && <AdminManager />}
          {!isPlatformAdmin && (
            <div style={panelStyle}>
              <p style={{ color: '#6a6252' }}>Only platform admins can promote or remove Platinum+ Admins.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const IntroVideoManager = () => {
  const [video, setVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/platinum-plus/admin/intro-video`); setVideo(r.data.video); } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/platinum-plus/admin/intro-video`, fd);
      setVideo(r.data.video); toast.success('Intro video uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remove the intro video?')) return;
    try { const r = await axios.delete(`${API}/platinum-plus/admin/intro-video`); setVideo(r.data.video); toast.success('Removed'); }
    catch (e) { toast.error('Failed to remove'); }
  };

  return (
    <div style={panelStyle} data-testid="platinum-intro-manager">
      <h3 style={{ fontWeight: 700, fontSize: '1.3rem', color: '#2A2620', display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.25rem' }}>
        <Video className="w-5 h-5" style={{ color: '#C9A84C' }} /> First-Entry Intro Video
      </h3>
      <p style={{ color: '#6a6252', fontSize: '.95rem', marginBottom: '1rem' }}>
        Plays full-screen the first time a member enters Platinum+ (once only). Max 500MB.
      </p>
      {video?.url ? (
        <div style={{ marginBottom: '1rem' }}>
          <video src={`${process.env.REACT_APP_BACKEND_URL}${video.url}`} controls style={{ width: '100%', maxWidth: 460, borderRadius: 10, border: '1px solid rgba(201,168,76,0.4)' }} data-testid="platinum-intro-preview" />
          <p style={{ color: '#2f7a41', fontSize: '.85rem', marginTop: '.4rem' }}>✓ {video.filename}</p>
        </div>
      ) : (
        <p style={{ color: '#8a8172', fontStyle: 'italic', marginBottom: '1rem' }}>No intro video yet — members go straight to the Sanctuary.</p>
      )}
      <input ref={inputRef} type="file" accept="video/*" className="hidden" data-testid="platinum-intro-input"
        onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ''; }} />
      <div style={{ display: 'flex', gap: '.5rem' }}>
        <button className="pp-btn-gold" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="platinum-intro-upload"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', background: 'linear-gradient(135deg,#C9A84C,#B9982F)', color: '#fff', fontWeight: 600, padding: '.55rem 1.1rem', borderRadius: 999 }}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {video?.url ? 'Replace' : 'Upload'}
        </button>
        {video?.url && (
          <button onClick={remove} data-testid="platinum-intro-delete"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', border: '1px solid #d9a', color: '#b34', padding: '.55rem 1rem', borderRadius: 999 }}>
            <Trash2 className="w-4 h-4" /> Remove
          </button>
        )}
      </div>
    </div>
  );
};

const AdminManager = () => {
  const [admins, setAdmins] = useState([]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);

  const loadAdmins = useCallback(async () => {
    try { const r = await axios.get(`${API}/platinum-plus/admins`); setAdmins(r.data); } catch (e) {}
  }, []);
  useEffect(() => { loadAdmins(); }, [loadAdmins]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await axios.get(`${API}/platinum-plus/admins/search?q=${encodeURIComponent(q)}`); setResults(r.data); } catch (e) {}
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const add = async (u) => {
    try { await axios.post(`${API}/platinum-plus/admins`, { user_id: u.id }); toast.success(`${u.full_name || u.username} is now a Platinum+ Admin`); setQ(''); setResults([]); loadAdmins(); }
    catch (e) { toast.error('Failed to add'); }
  };
  const remove = async (u) => {
    try { await axios.delete(`${API}/platinum-plus/admins/${u.id}`); toast.success('Removed'); loadAdmins(); }
    catch (e) { toast.error('Failed to remove'); }
  };

  return (
    <div style={panelStyle} data-testid="platinum-admin-manager">
      <h3 style={{ fontWeight: 700, fontSize: '1.3rem', color: '#2A2620', display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.75rem' }}>
        <Shield className="w-5 h-5" style={{ color: '#C9A84C' }} /> Platinum+ Admins
      </h3>

      <div style={{ position: 'relative', marginBottom: '1rem' }}>
        <Search className="w-4 h-4" style={{ position: 'absolute', left: 10, top: 11, color: '#b0a690' }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members by name or email…" data-testid="platinum-admin-search"
          style={{ width: '100%', padding: '.55rem .6rem .55rem 2rem', borderRadius: 10, border: '1px solid rgba(201,168,76,0.4)', background: 'rgba(255,255,255,0.7)' }} />
        {results.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 10, marginTop: 4, zIndex: 5, maxHeight: 240, overflowY: 'auto' }}>
            {results.map((u) => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.5rem .75rem', borderBottom: '1px solid #f0ead9' }} data-testid={`platinum-search-result-${u.id}`}>
                <span style={{ color: '#2A2620' }}>{u.full_name || u.username} <span style={{ color: '#a09880', fontSize: '.85rem' }}>{u.email}</span></span>
                {u.platinum_admin ? <span style={{ color: '#2f7a41', fontSize: '.85rem' }}>Already admin</span> :
                  <button onClick={() => add(u)} data-testid={`platinum-add-admin-${u.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '.25rem', color: '#B9982F', fontWeight: 600 }}><Plus className="w-4 h-4" /> Add</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {admins.length === 0 ? (
        <p style={{ color: '#8a8172', fontStyle: 'italic' }}>No Platinum+ Admins yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
          {admins.map((u) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.5rem .75rem', background: 'rgba(201,168,76,0.08)', borderRadius: 10 }} data-testid={`platinum-admin-row-${u.id}`}>
              <span style={{ color: '#2A2620' }}>{u.full_name || u.username} <span style={{ color: '#a09880', fontSize: '.85rem' }}>{u.email}</span></span>
              <button onClick={() => remove(u)} data-testid={`platinum-remove-admin-${u.id}`} style={{ color: '#b34', display: 'inline-flex', alignItems: 'center', gap: '.25rem' }}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PlatinumAdmin;
